/**
 * AssetStatusHistoryTab Component
 *
 * Displays a timeline of asset lifecycle events including creation and status changes.
 * Shows who made changes, when they occurred, and status transitions with visual indicators.
 *
 * Follows the same pattern as SeedHistoryTab.
 */

import { UserCircleIcon } from '@heroicons/react/24/outline';
import { DashedTag } from '@/components/chariot-ui';

import { Timeline } from '@/components/Timeline';
import { NoData } from '@/components/legacy-ui/NoData';
import { useGenericSearch } from '@/hooks/useGenericSearch';
import { Asset } from '@/types';
import { formatDate } from '@/utils/date.util';
import { ASSET_STATUS_DISPLAY_LABEL } from '@/utils/entities/asset.util';
import { getAccessEmail } from '@/utils/getAccessEmail';

interface AssetStatusHistoryTabProps {
  /** The asset to display status history for */
  asset: Asset;
}

/**
 * AssetStatusHistoryTab displays asset lifecycle events in a timeline.
 *
 * Shows the creation event and all subsequent status changes with:
 * - User who performed the action
 * - Timestamp of the change
 * - Status transition (from -> to) with visual tags
 */
export function AssetStatusHistoryTab({ asset }: AssetStatusHistoryTabProps) {
  const { data: accessGeneric } = useGenericSearch({
    query: '#access',
  });
  const accesss = accessGeneric?.accesss || [];

  const statusChanges =
    asset.history?.filter(({ from, to, updated, filePath }) => {
      // Only status changes: has from/to fields, updated timestamp, not an affiliation entry
      return !!from && !!to && !!updated && !filePath;
    }) ?? [];

  const hasHistory = statusChanges.length > 0 || asset.created;

  if (!hasHistory) {
    return (
      <NoData
        icon={<UserCircleIcon className="text-chariot-stroke size-[100px]" />}
        title="No Status Changes Recorded"
      />
    );
  }

  const resolvedSource = getAccessEmail(accesss, asset.source);
  const displaySource =
    !resolvedSource || resolvedSource === 'self' ? 'Guard' : resolvedSource;

  return (
    <Timeline
      items={[
        {
          title: (
            <p>
              <span className="font-bold">Discovered by</span> {displaySource}
            </p>
          ),
          updated: asset.created,
          description: asset.created && formatDate(asset.created),
          className: 'text-default',
        },
        ...statusChanges.map(item => {
          const by = getAccessEmail(accesss, item.by);

          return {
            title: (
              <div>
                <span>{!by || by === 'self' ? 'Guard' : by}</span> changed the{' '}
                <span className="text-base font-bold">status</span> from{' '}
                <DashedTag
                  size="xs"
                  label={ASSET_STATUS_DISPLAY_LABEL[item.from] || item.from}
                />{' '}
                to{' '}
                <DashedTag
                  size="xs"
                  label={ASSET_STATUS_DISPLAY_LABEL[item.to] || item.to}
                />
              </div>
            ),
            updated: item.updated,
            description: formatDate(item.updated),
            className: 'text-default',
          };
        }),
      ].reverse()}
    />
  );
}
